import { signUpInputSchema } from '@repo/api-contracts';
import { useState } from 'react';
import { useAuthStore } from '@/entities/user';
import { trpc } from '@/shared/api';
import { firstErrorPerField, getErrorMessage, mapServerValidationErrors } from '@/shared/lib';

const FIELD_KEYS = ['email', 'password', 'name'] as const;

const AGREEMENT_KEYS = ['termsOfService', 'privacyPolicy', 'ageOver19'] as const;

type FieldErrors = Partial<Record<(typeof FIELD_KEYS)[number], string>>;
type AgreementErrors = Partial<Record<(typeof AGREEMENT_KEYS)[number], string>>;

/**
 * 검증 전 폼 입력. 스키마(SignUpInput)는 필수 약관을 true 리터럴로 강제하므로
 * 체크박스 상태(boolean)를 그대로 받는 별도 타입이 필요하다 — 검증은 safeParse 가 한다.
 */
export interface SignUpFormValues {
  readonly email: string;
  readonly password: string;
  readonly name: string;
  readonly agreements: {
    readonly termsOfService: boolean;
    readonly privacyPolicy: boolean;
    readonly ageOver19: boolean;
    readonly marketingEmail: boolean;
    readonly marketingSms: boolean;
  };
}

interface UseSignUpResult {
  readonly fieldErrors: FieldErrors;
  readonly agreementErrors: AgreementErrors;
  readonly formError: string | null;
  readonly isPending: boolean;
  readonly submit: (input: SignUpFormValues) => Promise<boolean>;
}

/**
 * 회원가입 폼 로직. 필수 약관 3종은 스키마의 true 리터럴이 검증한다 —
 * 미동의 시 해당 체크박스에 필드 오류로 표시된다.
 */
export const useSignUp = (): UseSignUpResult => {
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [agreementErrors, setAgreementErrors] = useState<AgreementErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const setSession = useAuthStore((state) => state.setSession);
  const mutation = trpc.auth.signUp.useMutation();

  const submit = async (input: SignUpFormValues): Promise<boolean> => {
    setFormError(null);

    const parsed = signUpInputSchema.safeParse(input);

    if (!parsed.success) {
      const flattened = parsed.error.flatten().fieldErrors;
      setFieldErrors(firstErrorPerField(flattened, FIELD_KEYS));

      // 약관 스키마는 중첩 객체라 flatten 의 agreements 묶음에서 항목별로 다시 찾는다.
      const nested = parsed.error.issues.filter((issue) => issue.path[0] === 'agreements');
      const byKey: AgreementErrors = {};
      for (const issue of nested) {
        const key = issue.path[1] as (typeof AGREEMENT_KEYS)[number] | undefined;
        if (key && AGREEMENT_KEYS.includes(key) && !byKey[key]) {
          byKey[key] = issue.message;
        }
      }
      setAgreementErrors(byKey);
      return false;
    }

    setFieldErrors({});
    setAgreementErrors({});

    try {
      const session = await mutation.mutateAsync(parsed.data);
      setSession(session);
      return true;
    } catch (error) {
      // 서버의 약관 오류는 묶음 키(agreements)로만 와서 어느 체크박스인지 모른다 — 칸 오류가 없으면 폼 오류로 보인다.
      const serverErrors = mapServerValidationErrors(error, FIELD_KEYS);

      if (serverErrors) {
        setFieldErrors(serverErrors.fieldErrors);
        setFormError(serverErrors.formError);
      } else {
        setFormError(getErrorMessage(error, '회원가입에 실패했습니다.'));
      }

      return false;
    }
  };

  return { fieldErrors, agreementErrors, formError, isPending: mutation.isPending, submit };
};
